import { Tag } from '@/components/Tag'
import { Link } from '@/components/Link'
import { ContactLink } from '@/components/ContactLink'
import { CardPricing } from '@/components/CardPricing'
import type { LinkField } from '@/types/api'

export type CommunityCardPricing = 'default' | 'hidden'

export interface CommunityCardProps {
	name: string
	imageUrl?: string
	phone?: string
	phoneHref?: string
	matchingUnits: number
	/** Text beside the matching unit count, e.g. "Matching Homes". */
	matchingUnitsLabel?: string
	totalPrice?: string
	baseRent?: string
	leaseTerm?: string
	hasFees?: boolean
	hasDisclaimerSymbol?: boolean
	pricingPreText?: string
	pricing?: CommunityCardPricing
	/** Target of the "Contact" link when pricing is hidden; rendered as plain text when absent. */
	contactLink?: LinkField | null
	/** Engrain asset ID, exposed as `data-property` so focus can return to this card. */
	propertyId?: string
	onClick?: () => void
	spacesClass?: string
	className?: string
}

export function CommunityCard ({
	name,
	imageUrl,
	phone,
	phoneHref,
	matchingUnits,
	matchingUnitsLabel = 'Matching Units',
	totalPrice,
	baseRent,
	leaseTerm,
	hasFees = true,
	hasDisclaimerSymbol = false,
	pricingPreText,
	pricing = 'default',
	contactLink,
	propertyId,
	onClick,
	spacesClass = '',
	className = ''
}: CommunityCardProps) {
	const cardClasses = [
		'spaces-community spaces:bg-canvas-base spaces:border spaces:border-canvas-3 spaces:rounded-16 spaces:flex spaces:flex-col spaces:cursor-pointer spaces:group spaces:transition-all spaces:duration-200 spaces:hover:shadow-sm-below spaces:outline-none spaces:focus-visible:ring-2 spaces:focus-visible:ring-brand-base spaces:focus-visible:ring-offset-2',
		pricing === 'hidden' && 'spaces-no-pricing',
		spacesClass,
		className
	]
		.filter(Boolean)
		.join(' ')

	// Hidden pricing keeps the footer height of the matching default variant.
	const pricingPadding = pricing === 'hidden' && hasFees ? 'spaces:py-24' : 'spaces:py-16'

	return (
		<div
			className={cardClasses}
			data-property={propertyId}
			tabIndex={0}
			onClick={onClick}
			onKeyDown={e => {
				if ((e.key === 'Enter' || e.key === ' ') && onClick) {
					e.preventDefault()
					onClick()
				}
			}}
		>
			{/* Image */}
			<div className='spaces-community-image spaces:relative spaces:aspect-video spaces:w-full spaces:overflow-hidden spaces:rounded-t-16 spaces:bg-image-bg'>
				{imageUrl && (
					<img
						src={imageUrl}
						alt={name}
						className='spaces:absolute spaces:inset-0 spaces:w-full spaces:h-full spaces:object-cover'
					/>
				)}
			</div>

			{/* Details */}
			<div className='spaces-community-details spaces:flex spaces:flex-col spaces:gap-16 spaces:p-16'>
				<div className='spaces:flex spaces:flex-col'>
					<span className='spaces-community-name spaces:text-18/24 spaces:font-semi spaces:text-content-1'>{name}</span>
					{phone && (
						<Link
							href={phoneHref}
							label={phone}
							defaultUnderline={false}
							spacesClass='spaces-community-phone'
							onClick={e => e.stopPropagation()}
						/>
					)}
				</div>

				<div className='spaces-community-availability spaces:flex spaces:items-center spaces:gap-8'>
					<Tag
						label={String(matchingUnits)}
						appearance='stroke'
						color='content2'
						size='md'
					/>
					<span className='spaces-community-matching-label spaces:text-16/24 spaces:font-default spaces:text-content-2'>
						{matchingUnitsLabel}
					</span>
				</div>
			</div>

			{/* Pricing */}
			<div className={`spaces-community-pricing spaces:mt-auto spaces:flex spaces:items-center spaces:gap-8 spaces:px-16 spaces:bg-canvas-4 spaces:border-t spaces:border-canvas-3 spaces:rounded-b-16 ${pricingPadding}`}>
				{pricing === 'default' ? (
					<CardPricing
						hasFees={hasFees}
						totalPrice={totalPrice}
						baseRent={baseRent}
						leaseTerm={leaseTerm}
						hasDisclaimerSymbol={hasDisclaimerSymbol}
						preText={pricingPreText}
					/>
				) : (
					<ContactLink postText='for Pricing' link={contactLink} asLink={!!contactLink} />
				)}
			</div>
		</div>
	)
}
